import type { TokenAcessoDto } from '@nexital/shared';
import type { FonteToken } from './portas';

/** Chamadas de login, renovação e saída (o token de renovação vai e volta no cookie httpOnly). */
export interface ApiAcesso {
  /** Lança ErroApi (401) com e-mail ou senha inválidos. */
  entrar(email: string, senha: string): Promise<TokenAcessoDto>;
  /** null quando não há sessão (cookie ausente, vencido, revogado ou parado além do limite de inatividade). */
  renovar(atividadeEm?: number): Promise<TokenAcessoDto | null>;
  sair(): Promise<void>;
}

export type MensagemAbas =
  | { tipo: 'token'; tokenAcesso: string; expiraEm: number }
  | { tipo: 'saiu' }
  | { tipo: 'atividade'; em: number };

/** Comunicação entre as abas do mesmo navegador (BroadcastChannel) e trava exclusiva (Web Locks). */
export interface CanalAbas {
  publicar(mensagem: MensagemAbas): void;
  ouvir(aoReceber: (mensagem: MensagemAbas) => void): void;
  exclusivo<T>(trabalho: () => Promise<T>): Promise<T>;
}

type TokenAtual = { token: string; expiraEm: number; recebidoEm: number };

const ANTECEDENCIA_MS = 60_000;
const ESPERA_MINIMA_MS = 5_000;
const NOVA_TENTATIVA_MS = 10_000;
/** Movimentos seguidos não inundam o canal: no máximo um aviso às outras abas a cada 10 s. */
const INTERVALO_ATIVIDADE_MS = 10_000;

/**
 * Sessão do front (RQ-ACE-010): o JWT fica só na memória; renova ao abrir a página e 1 minuto antes de
 * expirar. As abas compartilham o JWT e a saída; a trava garante uma renovação por vez entre as abas
 * (duas renovações com o mesmo cookie seriam tomadas por reuso e derrubariam a sessão).
 * O último movimento na tela também é compartilhado e vai na renovação (RQ-ACE-014): uma aba ativa mantém todas.
 */
export class GerenciadorSessao implements FonteToken {
  private atual: TokenAtual | null = null;
  private readonly ouvintes = new Set<(autenticado: boolean) => void>();
  private agendada: ReturnType<typeof setTimeout> | undefined;
  private renovando: Promise<boolean> | null = null;
  private atividade = Date.now();
  private atividadePublicada = 0;

  constructor(
    private readonly api: ApiAcesso,
    private readonly canal: CanalAbas,
  ) {
    canal.ouvir((m) => {
      if (m.tipo === 'token') this.guardar(m.tokenAcesso, m.expiraEm);
      else if (m.tipo === 'atividade') this.atividade = Math.max(this.atividade, m.em);
      else this.limpar();
    });
  }

  /** Último movimento na tela, nesta ou em outra aba. */
  get ultimaAtividade() {
    return this.atividade;
  }

  registrarAtividade() {
    this.atividade = Date.now();
    if (this.atividade - this.atividadePublicada < INTERVALO_ATIVIDADE_MS) return;
    this.atividadePublicada = this.atividade;
    this.canal.publicar({ tipo: 'atividade', em: this.atividade });
  }

  get token() {
    return this.atual?.token ?? null;
  }

  /** Avisa quando a aba fica conectada (true) ou desconectada (false). */
  assinar(ouvinte: (autenticado: boolean) => void) {
    this.ouvintes.add(ouvinte);
    return () => void this.ouvintes.delete(ouvinte);
  }

  iniciar() {
    return this.renovar();
  }

  async entrar(email: string, senha: string) {
    this.compartilhar(await this.api.entrar(email, senha));
  }

  renovar(): Promise<boolean> {
    this.renovando ??= this.renovarEntreAbas().finally(() => {
      this.renovando = null;
    });
    return this.renovando;
  }

  /** O servidor recusou o JWT e a renovação: esta aba volta ao login. */
  expirou() {
    this.limpar();
  }

  async sair() {
    await this.api.sair().catch(() => undefined);
    this.limpar();
    this.canal.publicar({ tipo: 'saiu' });
  }

  private renovarEntreAbas() {
    const pedidoEm = Date.now();
    return this.canal.exclusivo(async () => {
      // Outra aba renovou enquanto esperávamos a trava: o cookie já foi girado, basta usar o token dela.
      if (this.atual && this.atual.recebidoEm >= pedidoEm) return true;
      try {
        const novo = await this.api.renovar(this.atividade);
        if (novo) this.compartilhar(novo);
        else this.limpar();
        return novo !== null;
      } catch {
        this.agendar(NOVA_TENTATIVA_MS);
        return false;
      }
    });
  }

  private compartilhar({ tokenAcesso, expiraEmSegundos }: TokenAcessoDto) {
    const expiraEm = Date.now() + expiraEmSegundos * 1000;
    this.guardar(tokenAcesso, expiraEm);
    this.canal.publicar({ tipo: 'token', tokenAcesso, expiraEm });
  }

  private guardar(token: string, expiraEm: number) {
    const estava = this.atual !== null;
    this.atual = { token, expiraEm, recebidoEm: Date.now() };
    this.agendar(Math.max(ESPERA_MINIMA_MS, expiraEm - ANTECEDENCIA_MS - Date.now()));
    if (!estava) this.avisar(true);
  }

  private limpar() {
    const estava = this.atual !== null;
    this.atual = null;
    clearTimeout(this.agendada);
    if (estava) this.avisar(false);
  }

  private agendar(esperaMs: number) {
    clearTimeout(this.agendada);
    this.agendada = setTimeout(() => void this.renovar(), esperaMs);
  }

  private avisar(autenticado: boolean) {
    for (const ouvinte of this.ouvintes) ouvinte(autenticado);
  }
}
